// Shared
import { getTextWidth, formatCurrency } from '../shared/helper';

// Components
import Actions from '../components/listing/Actions/Actions';
import Filter from '../components/listing/Filter/Filter';
import FormInput from '../components/detail/FormInput/FormInput';

// Taxonomies
import { yearCore, yearFilter } from '../taxonomies/year';
import { countryCore, countryFilter } from '../taxonomies/country';
import { trimCore, trimFilter } from './trim';
import { modelCore, modelFilter } from '../taxonomies/model';
import {
  upholsteryTypeCore,
  upholsteryTypeFilter
} from '../taxonomies/upholsteryType';
import { statusCore, statusFilter } from '../taxonomies/status';

// Upholstery core
export function upholsteryCore() {
  const endpoint = '/upholsteries';
  const endpointByTrim = '/upholsteries/UpholsteryTrim';
  const endpointDescriptionVersion = '/descriptionversion/type/upholstery/id';
  const endpointPriceVersion = null;
  const detailId = 'upholsteryTrimId';

  const getDetailId = (data = null) => {
    if (!data) {
      return;
    }

    if (Array.isArray(data)) {
      return data[0][detailId];
    }

    return data[detailId];
  };

  return {
    endpoint,
    endpointByTrim,
    endpointDescriptionVersion,
    endpointPriceVersion,
    detailId,
    getDetailId: getDetailId
  };
}

// Upholstery listing
export function upholsteryListing() {

  // Initial sort
  const initialSort = { field: 'id', sort: 'asc' };

  // Initial filter values
  const initialFilters = { UpholsteryDescriptionSearchType: 'C', VehicleDescriptionSearchType: 'C', DerivativeCodeSearchType: 'C', CodeSearchType: 'C' };

  // Get columns
  // columnsCustomized is used in case there is a hidden column
  const getColumns = (rows, columnsCustomized) => {
    const columns =
      columnsCustomized.length > 0
        ? columnsCustomized
        : [
            {
              field: 'actions',
              headerName: 'Actions',
              sortable: false,
              renderCell: function (params) {
                return (
                  <Actions
                    typeOfData="data-type"
                    id={params.row.upholsteryTrimId}
                    endpoint="/upholstery"
                    deleteApiEndpoint="/upholsteries"
                    deleteApiPath={`UpholsteryTrim/${params.row.upholsteryTrimId}`}
                  />
                );
              },
              hideFromColumnsSelector: true
            },
            {
              field: 'id',
              headerName: 'Row',
              hide: true,
              hideFromColumnsSelector: true
            },
            {
              field: 'realId',
              headerName: 'ID',
              hide: true
            },
            {
              field: 'trimId',
              headerName: 'Trim ID',
              hide: true,
              hideFromColumnsSelector: true
            },
            { field: 'status', headerName: 'Status' },
            { field: 'country', headerName: 'Country' },
            { field: 'year', headerName: 'Year' },
            { field: 'code', headerName: 'Advanced Code' },
            { field: 'upholsteryCode', headerName: 'Upholstery Code' },
            { field: 'description', headerName: 'Description' },
            { field: 'type', headerName: 'Type' },
            {
              field: 'price',
              headerName: 'Price',
              valueFormatter: function (params) {
                return formatCurrency(params.value);
              }
            },
            {
              field: 'retailerNet',
              headerName: 'Retailer Net',
              valueFormatter: function (params) {
                return formatCurrency(params.value);
              }
            },
            { field: 'modelCode', headerName: 'Model Code' },
            { field: 'model', headerName: 'Model' },
            { field: 'derivativeCode', headerName: 'Derivative Code' },
            { field: 'derivativeDescription', headerName: 'Derivative Description' }
          ];

    const columnsResized = columns;

    columns.forEach((column, i) => {
      const id = column.field;
      let text = column.headerName;

      const textWidth = getTextWidth(text);
      columnsResized[i].width = textWidth;

      rows.forEach((row, j) => {
        if (row[id]) {
          let text = row[id];

          // Add extra chars that might appear on the price
          if (id === 'price' || id === 'retailerNet') {
            text += '$,.00';
          }

          const textWidth = getTextWidth(text);

          if (columnsResized[i].width < textWidth) {
            columnsResized[i].width = textWidth;
          }
        }
      });
    });

    return columnsResized;
  };

  // Get rows
  const getRows = (data) => {
    const rows = [];

    data.forEach((row, i) => {
      // Text/numeric values
      rows[i] = {
        id: row.rowNumber,
        realId: row.upholsteryId,
        upholsteryTrimId: row.upholsteryTrimId,
        status: row.statusDescription,
        code: row.upholsteryCode,
        upholsteryCode: row.upholsteryCodeSimple,
        country: row.country,
        year: row.year,
        derivativeCode: row.derivativeCode,
        price: row.upholsteryPrice,
        retailerNet: row.upholsteryRetailerNet,
        trimId: row.trimId,
        modelCode: row.modelCode,
      };

      // Object values
      const objects = {
        model: row.modelDescription,
        derivativeDescription: row.trimDescription,
        type: row.upholsteryType,
        description: row.upholsteryDescription
      };

      // Get the text/value from the default culture
      Object.entries(objects).forEach(([j, prop]) => {
        prop.translations.forEach((culture, k) => {
          if (culture.defaultCulture === true) {
            rows[i][j] = culture.text;
          }
        });
      });
    });

    return rows;
  };

  // Get filters
  const getFilters = () => {
    // Taxonomies
    const countryCore_ = countryCore();
    const countryFilter_ = countryFilter();

    const yearCore_ = yearCore();
    const yearFilter_ = yearFilter();

    const modelCore_ = modelCore();
    const modelFilter_ = modelFilter();

    const statusCore_ = statusCore();
    const statusFilter_ = statusFilter();

    return [
      [
        {
          size: { xs: 12, md: 6, lg: 3 },
          component: (
            <Filter
              type="select"
              parameter={countryFilter_.parameter}
              label="Country"
              endpoint={countryCore_.endpoint}
              getRows={countryFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, md: 6, lg: 3 },
          component: (
            <Filter
              type="select"
              parameter={yearFilter_.parameter}
              label="Year"
              endpoint={yearCore_.endpoint}
              getRows={yearFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, md: 6, lg: 3 },
          component: (
            <Filter
              type="select"
              parameter={modelFilter_.parameter}
              label="Model"
              endpoint={modelCore_.endpointSimple}
              getRows={modelFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, md: 6, lg: 3 },
          component: (
            <Filter
              type="select"
              parameter={statusFilter_.parameter}
              label="Status"
              endpoint={statusCore_.endpoint}
              getRows={statusFilter_.getRows}
            />
          )
        }
      ],
      [
        {
          size: { xs: 12, md: 12, lg: 6 },
          component: (
            <Filter
              type="duo"
              parameter={['DerivativeCodeSearchType', 'DerivativeCode']}
              label="Derivative Code"
            />
          )
        },
        {
          size: { xs: 12, md: 12, lg: 6 },
          component: (
            <Filter
              type="duo"
              parameter={['VehicleDescriptionSearchType', 'VehicleDescription']}
              label="Vehicle Description"
            />
          )
        },
        {
          size: { xs: 12, md: 12, lg: 6 },
          component: (
            <Filter
              type="duo"
              parameter={['CodeSearchType', 'Code']}
              label="Advanced Code"
            />
          )
        },
        {
          size: { xs: 12, md: 12, lg: 6 },
          component: (
            <Filter
              type="duo"
              parameter={[
                'UpholsteryDescriptionSearchType',
                'UpholsteryDescription'
              ]}
              label="Description"
            />
          )
        }
      ]
    ];
  };

  return {
    initialSort,
    initialFilters,
    getColumns: getColumns,
    getRows: getRows,
    getFilters: getFilters
  };
}

// Upholstery detail
export function upholsteryDetail() {
  const baseUrl = '/upholstery';

  const watchedParameters = {
    yearId: null,
    trimId: null
  };

  const generateUpholsteryCode = (
    yearId,
    trimId
  ) => {
    if (
      trimId &&
      trimId.code &&
      yearId &&
      yearId.label
    ) {
      return `${yearId.label}-${trimId.code}-R*****`;
    } else {
      return '';
    }
  };

  const getInputs = (data = null) => {
    // set an object of all the field values
    // where the default state is empty for each
    let detailObj = {
      upholsteryId: '',
      upholsteryCode: '',
      upholsteryDescription: '',
      upholsteryDescriptionText: '',
      upholsteryTypeId: '',
      upholsteryEnabled: false,
      countryId: '',
      yearId: '',
      modelId: '',
      trimId: '',
      upholsteryPrice: 0,
      upholsteryRetailerNet: 0,
      priceMajorVersion: 0,
      priceMinorVersion: 0
    };

    if (data !== null) {
      // Use the data provided to populate the default value of the fields
      Object.entries(detailObj).forEach(([i, prop]) => {
        detailObj[i] = data[i];
      });

      // Get the text/value from the default culture
      detailObj.upholsteryDescription.translations.forEach((culture, k) => {
        if (culture.defaultCulture === true) {
          detailObj.upholsteryDescriptionText = culture.text;
        }
      });
    }

    // Taxonomies
    const countryCore_ = countryCore();
    const countryFilter_ = countryFilter();

    const yearCore_ = yearCore();
    const yearFilter_ = yearFilter();

    const modelCore_ = modelCore();
    const modelFilter_ = modelFilter();

    const trimCore_ = trimCore();
    const trimFilter_ = trimFilter();

    const upholsteryTypeCore_ = upholsteryTypeCore();
    const upholsteryTypeFilter_ = upholsteryTypeFilter();

    const upholsteryCore_ = upholsteryCore();

    return [
      // Data Version (description.MajorVersion + description.MinorVersion)
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          className: "version-item-wrapper",
          component: (
            <FormInput
              type="version-control"
              inputProps={{
                itemType: "data-type",
                versionType: "description",
                versionLabel: "Data Version",
                endPoint: upholsteryCore_.endpointDescriptionVersion,
                itemId: detailObj.upholsteryId,
                majorVersion: detailObj.upholsteryDescription.majorVersion,
                minorVersion: detailObj.upholsteryDescription.minorVersion,
                dialogueTitle: "Data Version",
                ctaLabel: "Edit Data version"
              }}
            />
          )
        }
      ],
      // Price Version (priceMajorVersion + priceMinorVersion)
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          className: "version-item-wrapper",
          component: (
            <FormInput
              type="version-control"
              inputProps={{
                itemType: "data-type",
                versionType: "price",
                versionLabel: "Price Version",
                endPoint: upholsteryCore_.endpointPriceVersion,
                itemId: detailObj.upholsteryId,
                majorVersion: detailObj.priceMajorVersion,
                minorVersion: detailObj.priceMinorVersion,
                dialogueTitle: "Price Version",
                ctaLabel: "Edit Price version"
              }}
            />
          )
        }
      ],
      // Country
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="select"
              parameter={countryCore_.detailId}
              label="Country"
              required={true}
              endpoint={countryCore_.endpoint}
              getRows={countryFilter_.getRows}
              inputValue={detailObj.countryId}
              isCulturesSource={true}
            />
          )
        }
      ],
      // Year
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="select"
              taxonomyName="year"
              parameter={yearCore_.detailId}
              label="Year"
              required={true}
              endpoint={yearCore_.endpoint}
              getRows={yearFilter_.getRows}
              inputValue={detailObj.yearId}
              dependOn={[{ filter: 'countryid', control: 'countryId' }]}
            />
          )
        }
      ],
      // Model
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="select"
              taxonomyName="model"
              parameter={modelCore_.detailId}
              label="Model"
              required={true}
              disabled={true}
              endpoint={modelCore_.endpoint}
              getRows={modelFilter_.getRows}
              inputValue={detailObj.modelId}
              dependOn={[
                { filter: 'countryid', control: 'countryId' },
                { filter: 'yearid', control: 'yearId' }
              ]}
            />
          )
        }
      ],
      // Trim
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="select-autocomplete"
              taxonomyName="trim"
              parameter={trimCore_.detailId}
              label="Vehicle"
              required={true}
              disabled={true}
              endpoint={trimCore_.endpoint}
              getRows={trimFilter_.getRows}
              inputValue={detailObj.trimId}
              dependOn={[
                { filter: 'countryid', control: 'countryId' },
                { filter: 'yearid', control: 'yearId' },
                { filter: 'modelid', control: 'modelId' }
              ]}
            />
          )
        }
      ],
      // Type
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="select"
              parameter={upholsteryTypeCore_.detailId}
              label="Type"
              required={true}
              endpoint={upholsteryTypeCore_.endpoint}
              getRows={upholsteryTypeFilter_.getRows}
              inputValue={detailObj.upholsteryTypeId}
            />
          )
        }
      ],
      // Description
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="localization"
              parameter="upholsteryDescription"
              label="Description"
              required={true}
              inputValue={{
                key: detailObj.upholsteryDescription.key
                  ? detailObj.upholsteryDescription.key
                  : '',
                translations: detailObj.upholsteryDescription.translations
                  ? detailObj.upholsteryDescription.translations
                  : [],
                defaultCultureText: detailObj.upholsteryDescriptionText
              }}
              dependOn={[{ filter: 'countryid', control: 'countryId' }]}
            />
          )
        }
      ],
      // Code
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="text"
              parameter="upholsteryCode"
              label="Advanced Code"
              required={true}
              inputValue={detailObj.upholsteryCode}
              generatedValueKey="generateUpholsteryCode"
            />
          )
        }
      ],
      // Enabled
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="checkbox"
              label="Enabled"
              parameter="upholsteryEnabled"
              required={true}
              inputValue={detailObj.upholsteryEnabled}
            />
          )
        }
      ],
      // Price
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="price"
              priceType="price"
              parameter="upholsteryPrice"
              label="Price"
              required={true}
              inputValue={detailObj.upholsteryPrice}
            />
          )
        }
      ],
      // Retailer Net
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="price"
              priceType="retailerNet"
              parameter="upholsteryRetailerNet"
              label="Retailer Net"
              required={false}
              inputValue={detailObj.upholsteryRetailerNet}
            />
          )
        }
      ]
    ];
  };

  return {
    baseUrl,
    getInputs: getInputs,
    watchedParameters: watchedParameters,
    generateUpholsteryCode: generateUpholsteryCode
  };
}
